// Ported from deexoptions-mobile-app/components/DashboardComps/SurveyBanner.tsx.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChatbubbleEllipsesOutline, IoClose } from "react-icons/io5";
import { useActiveSurvey, useRecordSurveyView } from "../../hooks/queries/useSurvey";

/**
 * Survey ids closed from the dashboard in this browser tab.
 *
 * Deliberately `sessionStorage`, not the server's `/dismiss`: dismissing is permanent *and*
 * global, so it would silently kill the settings and transaction-success cards too. Closing
 * the dashboard banner is the softer "not right now" — it stays gone for this tab and comes
 * back on the next visit. `localStorage` would make a soft close permanent, which is the
 * one thing this must not do. (Mobile uses a module-level Set, recreated when the JS
 * context restarts; a tab's lifetime is the closest web equivalent.)
 */
const CLOSED_KEY = "surveyBannerClosed";

const readClosed = (): string[] => {
  try {
    const raw = sessionStorage.getItem(CLOSED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    // Private mode or blocked storage: the banner simply reappears, which is harmless.
    return [];
  }
};

const addClosed = (id: string) => {
  try {
    const next = Array.from(new Set([...readClosed(), id]));
    sessionStorage.setItem(CLOSED_KEY, JSON.stringify(next));
  } catch {
    // Non-fatal; the in-memory `closed` state still hides it for this render.
  }
};

/**
 * Survey ids whose view has already been reported in this page load.
 *
 * The dashboard mounts two instances of this banner — one for narrow widths, one for the
 * desktop two-column layout — and only one of them is visible at a time. A per-component
 * ref would let each fire its own POST; this is shared, so the view is reported once.
 * (The endpoint is unique-per-user server-side, so this is tidiness rather than
 * correctness, but there is no reason to send the second request.)
 */
const recordedViews = new Set<string>();

type Props = {
  enabled?: boolean;
  /** Lets the dashboard place and show/hide each instance per breakpoint. */
  className?: string;
};

/**
 * The dashboard's survey prompt, shown only when the admin gave the survey the
 * DASHBOARD_CTA placement. Renders nothing otherwise — including once the user has
 * completed or dismissed it, since the backend then stops returning it.
 */
const SurveyBanner: React.FC<Props> = ({ enabled = true, className = "" }) => {
  const navigate = useNavigate();
  const { data: survey } = useActiveSurvey("DASHBOARD_CTA", enabled);
  const recordView = useRecordSurveyView();

  const [closed, setClosed] = useState(false);

  // Counted once per user server-side; the refs just avoid re-firing on every render
  // (`viewed`) and across the two mounted instances (`recordedViews`).
  const viewed = useRef<string | null>(null);
  useEffect(() => {
    if (!survey?._id || viewed.current === survey._id) return;
    if (recordedViews.has(survey._id)) return;
    viewed.current = survey._id;
    recordedViews.add(survey._id);
    recordView.mutate(survey._id);
    // recordView is a stable mutation object; depending on it would re-fire the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey?._id]);

  if (!survey || closed || readClosed().includes(survey._id)) return null;

  const onClose = (e: React.MouseEvent) => {
    // The card itself is clickable, so the × must not also open the survey.
    e.stopPropagation();
    addClosed(survey._id);
    setClosed(true);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/survey?placement=DASHBOARD_CTA")}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate("/survey?placement=DASHBOARD_CTA");
        }
      }}
      // No width utility here on purpose: a block element already fills its container, so
      // a caller can inset it with `mx-*` without fighting a `w-full`.
      className={`cursor-pointer rounded-xl border border-secondary_button/40 bg-white px-4 py-4 dark:bg-[#1C2227] ${className}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D4EBFD] text-[18px] text-secondary_button dark:bg-secondary_button/20">
          <IoChatbubbleEllipsesOutline />
        </div>

        <div className="min-w-0 flex-1 pr-2">
          <h4 className="font-sora text-[14px] font-semibold text-gray-900 dark:text-gray-200">
            Got a minute? Tell us what you think
          </h4>
          <p className="mt-1 text-[12px] text-gray-500 dark:text-gray-400">
            {survey.rewardPoints
              ? `Answer a few quick questions and earn ${survey.rewardPoints} points`
              : survey.description ||
                "Answer a few quick questions to help us improve"}
          </p>
        </div>

        {/* Padded past the icon's own bounds so the close target stays comfortably tappable. */}
        <button
          type="button"
          aria-label="Close survey prompt"
          onClick={onClose}
          className="-mr-1 self-start p-2 text-[18px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <IoClose />
        </button>
      </div>
    </div>
  );
};

export default SurveyBanner;
